import { useState, useEffect } from 'react';
import api from '../../../api/client';

const EMPTY_FORM = { workerId: '', period: '', hours: '' };

export default function TimecardsPage() {
  const [timecards,  setTimecards]  = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [form,       setForm]       = useState(EMPTY_FORM);
  const [saving,     setSaving]     = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get('/vms/timecards');
      setTimecards(data.data?.items || []);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  function patch(key, val) {
    setForm((f) => ({ ...f, [key]: val }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await api.post('/vms/timecards', {
        workerId: form.workerId,
        period:   form.period,
        hours:    Number(form.hours),
      });
      setForm(EMPTY_FORM);
      setSubmitOpen(false);
      await load();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleApprove(id) {
    setError(null);
    try {
      await api.post(`/vms/timecards/${id}/approve`);
      await load();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    }
  }

  async function handleReject(id) {
    setError(null);
    try {
      await api.post(`/vms/timecards/${id}/reject`);
      await load();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    }
  }

  return (
    <div data-testid="vms-timecards-page" data-loading={loading ? 'true' : 'false'}>
      <h2>Timecards</h2>

      {error && <p style={{ color: 'red' }}>{error}</p>}

      <div style={{ marginBottom: '1rem' }}>
        <button data-testid="vms-timecard-submit" onClick={() => setSubmitOpen(true)}>
          Submit Timecard
        </button>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <table style={{ width: '100%' }}>
          <thead>
            <tr>
              <th>Worker</th>
              <th>Period</th>
              <th>Hours</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {timecards.map((tc) => (
              <tr key={tc.id} data-testid={`vms-timecard-row-${tc.id}`}>
                <td>{tc.worker_name || tc.worker_id}</td>
                <td>{tc.period}</td>
                <td>{tc.hours}</td>
                <td>{tc.status}</td>
                <td style={{ display: 'flex', gap: '0.5rem' }}>
                  {tc.status === 'submitted' && (
                    <>
                      <button
                        data-testid={`vms-timecard-approve-${tc.id}`}
                        onClick={() => handleApprove(tc.id)}
                      >
                        Approve
                      </button>
                      <button
                        data-testid={`vms-timecard-reject-${tc.id}`}
                        onClick={() => handleReject(tc.id)}
                      >
                        Reject
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {!timecards.length && <tr><td colSpan={5}>No timecards yet.</td></tr>}
          </tbody>
        </table>
      )}

      {submitOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 8, padding: '1.5rem', minWidth: 360 }}>
            <h3>Submit Timecard</h3>
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <input
                  placeholder="Worker ID"
                  value={form.workerId}
                  onChange={(e) => patch('workerId', e.target.value)}
                  required
                />
                <input
                  placeholder="Period (e.g. 2027-01)"
                  value={form.period}
                  onChange={(e) => patch('period', e.target.value)}
                  required
                />
                <input
                  placeholder="Hours"
                  type="number"
                  min={0}
                  step={0.5}
                  value={form.hours}
                  onChange={(e) => patch('hours', e.target.value)}
                  required
                />
              </div>
              {error && <p style={{ color: 'red', marginTop: '0.5rem' }}>{error}</p>}
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
                <button type="submit" data-testid="vms-timecard-form-submit" disabled={saving}>
                  {saving ? 'Submitting...' : 'Submit'}
                </button>
                <button type="button" onClick={() => { setSubmitOpen(false); setError(null); }}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
